import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import SectionTitle from '@/components/base/SectionTitle';
import { mediaList } from '@/mocks/media';
import { newsList } from '@/mocks/news';

type Tab = 'all' | 'movie' | 'tv';

const tabs: { key: Tab; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
];

export default function LatestSection() {
  const [tab, setTab] = useState<Tab>('all');

  const latest = useMemo(() => {
    const list = tab === 'all' ? mediaList : mediaList.filter((m) => m.type === tab);
    return [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, 8);
  }, [tab]);

  const hotRank = useMemo(
    () => [...mediaList].sort((a, b) => b.rating - a.rating).slice(0, 5),
    []
  );

  const topNews = useMemo(() => newsList.slice(0, 3), []);

  return (
    <section className="container-page pb-12">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        <div>
          <SectionTitle title="最新上线" icon="ri-time-line">
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`rounded-full px-3 py-1 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    tab === t.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </SectionTitle>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {latest.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>

        <aside className="space-y-8">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4">
            <SectionTitle title="热门资讯" icon="ri-newspaper-line" moreTo="/news" moreLabel="全部" />
            <ul className="space-y-3">
              {topNews.map((news) => (
                <li key={news.id}>
                  <Link
                    to={`/news/${news.id}`}
                    className="group block cursor-pointer"
                  >
                    <p className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-accent-600">
                      {news.title}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                      <i className="ri-calendar-line"></i>
                      {news.date}
                      <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                        {news.category}
                      </span>
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-4">
            <SectionTitle title="高分排行" icon="ri-bar-chart-2-line" />
            <ul className="space-y-3">
              {hotRank.map((item, i) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex items-center gap-3 cursor-pointer"
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold ${
                        i < 3
                          ? 'bg-accent-500 text-primary-950'
                          : 'bg-background-200 text-foreground-500'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {item.year} · {item.region}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-semibold text-accent-600">
                      {item.rating.toFixed(1)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}